import { useCallback, useEffect, useState } from 'react';
import { fetchAdviseInvoiceFile } from '../../../../Api/Advise/Engagements';
import { SupplierInvoiceFileLinks } from '../../../../types/advise';
import { TextBlock } from '../../../Atoms/Text';
import { ComponentSpinner } from '../../Loading/ComponentSpinner';
import { LoadError } from '../LoadError';
import { InvoiceDownloadButton, InvoiceFileProps } from './InvoiceDownloadButton';

export type OriginalInvoicePanelProps = InvoiceFileProps;

type Load = { links: SupplierInvoiceFileLinks | null; failed: boolean };

// The links expire, so the preview reads them each time the tab opens rather than keeping them with the engagement.
const useInvoicePreviewLinks = ({ engagementId, supplierId, invoice }: OriginalInvoicePanelProps) => {
  const [load, setLoad] = useState<Load>({ links: null, failed: false });
  const retry = useCallback(async () => {
    setLoad({ links: null, failed: false });
    const { data } = await fetchAdviseInvoiceFile(engagementId, supplierId, invoice.id);
    setLoad({ links: data, failed: !data });
  }, [engagementId, supplierId, invoice.id]);

  useEffect(() => {
    if (invoice.has_file) retry();
  }, [invoice.has_file, retry]);

  return { ...load, retry };
};

/** A read-only preview of the uploaded invoice, with its Download. */
export const OriginalInvoicePanel = (props: OriginalInvoicePanelProps) => {
  const { links, failed, retry } = useInvoicePreviewLinks(props);
  const { invoice } = props;

  if (!invoice.has_file) {
    return (
      <TextBlock
        size="sm"
        color="grey6">
        The original file for this invoice is no longer available.
      </TextBlock>
    );
  }
  if (failed) {
    return (
      <LoadError
        subject="the original invoice"
        consequence="it cannot be previewed or downloaded"
        onRetry={retry}
      />
    );
  }
  if (!links) return <ComponentSpinner />;
  return (
    <div className="flex flex-col gap-3">
      <InvoiceDownloadButton {...props} />
      {/* The browser's own PDF viewer loads the file as a page, so the bucket needs no CORS rule for the portal.
          Session replay would record the src, a signed link to the client's invoice, unless it is hidden. */}
      <iframe
        title={invoice.file_name}
        src={links.url}
        data-dd-privacy="hidden"
        className="h-[80vh] w-full rounded-md border border-grey-2"
      />
    </div>
  );
};
